import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useBillingSubscription, useInvalidateBillingUsage } from "@/billing"
import {
  useReportsTransportInvalidation,
  useSaveReportTransport,
} from "@/reports"
import { COMPARE_BY_OPTION } from "@/run-detail/compare-by"
import { REPORT_VIEW_MODE } from "@/run-detail/report-view"
import { useRunTransportInvalidation } from "@/runs"
import { groupTestCaseSelection } from "@/test-set"
import { useListTestCasesTransport } from "@/test-set/transport"
import { useWorkspaceContext } from "@/workspaces/scope"
import { useUser } from "@clerk/nextjs"
import { toast } from "@coval/ui"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { logger } from "@/lib/logger"
import { useDevId } from "@/hooks/useDevId"
import type { SimulatorType } from "@/types/api/SimulatorType"
import type { JsonObject } from "@/types/json"

import { executeEvaluationLaunch } from "../utils/executeEvaluationLaunch"
import {
  buildEvaluationSummaryData,
  DEFAULT_CONCURRENCY,
  getAdjustedEvaluationConcurrency,
  getEvaluationValidationErrors,
  getRetainedSelectedTestCaseIds,
  hasMultipleEvaluationPersonas,
  hasMultipleEvaluationTestSets,
  isMultiRunEvaluation,
} from "./evaluationLauncherState"
import { getConfiguredMetricNames } from "./selectedMetricIds"
import type { LaunchResult } from "./types"
import { useEvaluationData } from "./useEvaluationData"
import { useEvaluationState } from "./useEvaluationState"
import { useLaunchConfig } from "./useLaunchConfig"
import { copyRunIds, useAgentSelectionChange } from "./useLauncherInteractions"
import { useRunMetadataEditor } from "./useRunMetadataEditor"

const log = logger("EvaluationLauncher")

interface UseEvaluationLauncherControllerParams {
  organizationId: string
  selectedTestSetId?: string
  selectedTestSetIds?: string[]
  selectedAgentId?: string
  selectedMetricNames?: string[]
  onNavigateToRun: (runIds: string[]) => void
  onNavigateToReport: (reportId: string) => void
  runId?: string
  /**
   * Where this simulate session was opened from (agent page, resimulate, copilot,
   * onboarding…). Drives the recursive-improvement attribution on
   * `simulate_run_launched`. Defaults to runId-based derivation.
   */
  launchSource?: string
}

export function useEvaluationLauncherController({
  organizationId,
  selectedTestSetId,
  selectedTestSetIds,
  selectedAgentId,
  selectedMetricNames,
  onNavigateToRun,
  onNavigateToReport,
  runId,
  launchSource,
}: UseEvaluationLauncherControllerParams) {
  const [templateRequest, setTemplateRequest] = useState({
    id: "",
    pending: false,
  })
  const {
    testSetRecords,
    metricRecords,
    templateRecords,
    organizationRecord,
    personaRecords,
    initialTemplateData,
    hasRunId,
    hasTemplateId,
    isLoading: dataLoading,
    hasError,
    refetchData,
  } = useEvaluationData(organizationId, templateRequest.id)
  const isLoading = dataLoading || templateRequest.pending

  const {
    templateData,
    metricSetupRevision,
    updateTemplateData,
    loadTemplate,
    clearTemplate,
    stableTemplateData,
  } = useEvaluationState({
    initialTemplateData: initialTemplateData || undefined,
    selectedTestSetId,
    selectedTestSetIds,
    selectedAgentId,
    selectedMetricNames,
    hasExternalTemplate: hasRunId || hasTemplateId,
    testSetRecords,
    metricRecords,
  })

  const prevRunIdRef = useRef<string | undefined>(runId)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(
    initialTemplateData?.templateId || "",
  )
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false)
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const [scheduledConfig, setScheduledConfig] = useState<{
    frequency: string
  } | null>(null)
  const [launchAttempted, setLaunchAttempted] = useState(false)
  const [isLaunching, setIsLaunching] = useState(false)
  const [runDisplayName, setRunDisplayName] = useState("")
  const internalDevId = useDevId()
  const [tags, setTags] = useState<string[]>([])
  const [createReport, setCreateReport] = useState(false)
  const [reportName, setReportName] = useState("")

  useEffect(() => {
    if (prevRunIdRef.current && !runId) {
      clearTemplate()
      setSelectedTemplateId("")
      setTemplateRequest({ id: "", pending: false })
    }
    prevRunIdRef.current = runId
  }, [runId, clearTemplate])

  const { user } = useUser()
  const username = user?.fullName || user?.username || "Unknown User"
  const { currentWorkspace } = useWorkspaceContext()
  const workspaceId = currentWorkspace?.id
  const { trackEvent } = useAnalytics()
  const { invalidateRunsLists } = useRunTransportInvalidation()
  const invalidateBillingUsage = useInvalidateBillingUsage()
  const saveReportMutation = useSaveReportTransport()
  const reportsInvalidation = useReportsTransportInvalidation()

  const { data: billingSubscription, status: subscriptionStatus } =
    useBillingSubscription()
  // Hold launch only while quota resolves; errors defer to the backend quota response.
  const isQuotaResolving = subscriptionStatus === "pending"
  const metricsPerCallLimit =
    billingSubscription?.quota?.metrics_per_call_limit ?? null

  const configuredMetricNames = useMemo(
    () => getConfiguredMetricNames(templateData.enabledMetrics, metricRecords),
    [templateData.enabledMetrics, metricRecords],
  )
  const resolvedMetricsCount = useMemo(
    () =>
      configuredMetricNames.filter(
        (metricName) => metricRecords[metricName]?.id,
      ).length,
    [configuredMetricNames, metricRecords],
  )

  const runMetadata = useRunMetadataEditor({
    initialTemplateData: initialTemplateData || undefined,
    sourceRunId: runId,
  })

  const hasExplicitCaseSelection = Boolean(
    templateData.selectedTestCaseIds?.length,
  )
  const testCaseCatalog = useListTestCasesTransport({
    enabled: hasExplicitCaseSelection,
  })
  const caseSelection = useMemo(
    () =>
      groupTestCaseSelection(
        templateData.selectedTestCaseIds ?? [],
        testCaseCatalog.data ?? [],
      ),
    [templateData.selectedTestCaseIds, testCaseCatalog.data],
  )

  const validationErrors = useMemo(() => {
    const errors = getEvaluationValidationErrors(
      { ...templateData, enabledMetrics: configuredMetricNames },
      metricsPerCallLimit,
      resolvedMetricsCount,
    )

    if (hasExplicitCaseSelection) {
      if (testCaseCatalog.error) {
        errors.push({
          message:
            "Could not load selected test cases. Retry in the Test Cases picker.",
        })
      } else if (!testCaseCatalog.data) {
        errors.push({ message: "Loading selected test cases…" })
      } else if (
        caseSelection.missingIds.length > 0 ||
        templateData.testSetIds.some(
          (id) => !caseSelection.byTestSet[id]?.length,
        ) ||
        caseSelection.testSetIds.some(
          (id) => !templateData.testSetIds.includes(id),
        )
      ) {
        errors.push({
          message:
            "Some selected test cases are unavailable or have moved. Review your selection.",
        })
      }
    }
    if (runMetadata.error) {
      errors.push({ message: runMetadata.error })
    }

    return errors
  }, [
    templateData,
    metricsPerCallLimit,
    resolvedMetricsCount,
    configuredMetricNames,
    runMetadata.error,
    hasExplicitCaseSelection,
    testCaseCatalog.error,
    testCaseCatalog.data,
    caseSelection,
  ])

  const { generateLaunchRunRequestForAgent, isLaunchDisabled } =
    useLaunchConfig({
      templateData,
      selectedTestCaseIdsByTestSet: caseSelection.byTestSet,
      testSetRecords,
      metricRecords,
      personaRecords,
      username,
      validationErrors,
      isLoading,
      runDisplayName,
      tags,
      customerMetadata: runMetadata.customerMetadata,
    })
  const executeLaunch = useCallback(async (): Promise<LaunchResult> => {
    setLaunchAttempted(true)
    if (validationErrors.length > 0) {
      return {
        success: false,
        runIds: [],
        error: validationErrors.map(({ message }) => message).join(" "),
      }
    }

    const result = await executeEvaluationLaunch({
      agentIds: templateData.agentIds || [],
      personaIds: templateData.personaIds || [],
      testSetIds: templateData.testSetIds || [],
      shouldSubSample: Boolean(templateData.shouldSubSample),
      selectedMutationIds: templateData.mutationIdsByAgent ?? {},
      metricLabelsById: Object.fromEntries(
        Object.values(metricRecords).map((metric) => [
          metric.id,
          metric.metric_display_name || metric.metric_name,
        ]),
      ),
      generateLaunchRunRequest: generateLaunchRunRequestForAgent,
      internalDevId,
      workspaceId,
    })

    return result
  }, [
    templateData.agentIds,
    templateData.personaIds,
    templateData.testSetIds,
    templateData.shouldSubSample,
    templateData.mutationIdsByAgent,
    metricRecords,
    generateLaunchRunRequestForAgent,
    internalDevId,
    workspaceId,
    validationErrors,
  ])

  // Closes over templateData snapshot at call-time so the funnel records
  // *what was launched* (counts, not IDs) without leaking org-internal data.
  // We track only on success because a failed launch isn't user-meaningful
  // activity — the funnel question is "did they actually run a sim".
  const trackRunLaunched = useCallback(
    (runIds: string[]) => {
      const isRerun = Boolean(runId)
      trackEvent(TRACKING_EVENTS.SIMULATE.RUN_LAUNCHED, {
        run_count: runIds.length,
        agent_count: (templateData.agentIds || []).length,
        persona_count: (templateData.personaIds || []).length,
        test_set_count: (templateData.testSetIds || []).length,
        metric_count: (templateData.enabledMetrics || []).length,
        used_template: Boolean(selectedTemplateId || templateData.templateId),
        // Recursive-improvement attribution: a run prefilled from a prior run
        // is the loop taking another turn.
        is_rerun: isRerun,
        launch_source:
          launchSource || (isRerun ? "resimulate" : "simulate_page"),
        prefilled_from_run_id: runId,
      })
    },
    [
      trackEvent,
      templateData.agentIds,
      templateData.personaIds,
      templateData.testSetIds,
      templateData.enabledMetrics,
      templateData.templateId,
      selectedTemplateId,
      runId,
      launchSource,
    ],
  )

  // A launch spends allowance as well as producing runs, and the launcher stays
  // mounted through "Launch & Create Another", so the usage the chip reads has
  // to be dropped alongside the run lists rather than waiting out its stale
  // window on numbers the launch just made wrong.
  const invalidatePostLaunchCaches = useCallback(() => {
    void invalidateRunsLists().catch((error: unknown) => {
      log.warn("Failed to invalidate run list caches", { error })
    })
    void invalidateBillingUsage().catch((error: unknown) => {
      log.warn("Failed to invalidate billing usage cache", { error })
    })
  }, [invalidateBillingUsage, invalidateRunsLists])

  // Creates a single report bundling every run just launched. Returns the new
  // report id on success, or null so the caller can fall back to the runs page.
  const createReportFromRuns = useCallback(
    async (runIds: string[]): Promise<string | null> => {
      try {
        const result = await saveReportMutation.mutateAsync({
          display_name:
            reportName.trim() || runDisplayName.trim() || "Untitled Report",
          run_ids: runIds,
          view_config: {
            compareBy: "run",
            metadataKey: null,
            viewMode: REPORT_VIEW_MODE.GROUPED,
            secondaryCompareBy: COMPARE_BY_OPTION.NONE,
            secondaryMetadataKey: null,
          },
        })
        await Promise.all([
          reportsInvalidation.invalidateList(),
          reportsInvalidation.invalidateReport(result.ulid),
        ]).catch((error: unknown) => {
          log.warn("Failed to invalidate report caches", { error })
        })
        return result.ulid
      } catch (error) {
        log.error("Failed to create report from launched runs", error)
        toast.danger({
          title: "Report Not Created",
          description:
            "Your runs launched, but the combined report could not be created.",
          duration: 5000,
        })
        return null
      }
    },
    [reportName, runDisplayName, saveReportMutation, reportsInvalidation],
  )

  const handleLaunch = useCallback(async () => {
    if (isLaunching) {
      return
    }

    setIsLaunching(true)

    try {
      const result = await executeLaunch()

      if (result.success && result.runIds.length > 0) {
        trackRunLaunched(result.runIds)
        invalidatePostLaunchCaches()

        if (createReport && result.runIds.length > 1) {
          const reportId = await createReportFromRuns(result.runIds)
          if (reportId) {
            onNavigateToReport(reportId)
            return
          }
        }

        onNavigateToRun(result.runIds)
      } else if (!result.success && result.error) {
        toast.danger({
          title: "Launch Failed",
          description: result.error,
          duration: 5000,
        })
      }
    } finally {
      setIsLaunching(false)
    }
  }, [
    createReport,
    createReportFromRuns,
    executeLaunch,
    invalidatePostLaunchCaches,
    isLaunching,
    onNavigateToReport,
    onNavigateToRun,
    trackRunLaunched,
  ])

  const handleLaunchAndCreateAnother = useCallback(async () => {
    if (isLaunching) {
      return
    }

    setIsLaunching(true)

    try {
      const result = await executeLaunch()

      if (result.success && result.runIds.length > 0) {
        trackRunLaunched(result.runIds)
        invalidatePostLaunchCaches()
        void copyRunIds(result.runIds)
        toast.success({
          title: "Simulation Launched",
          description: `Run ID${result.runIds.length > 1 ? "s" : ""}: ${result.runIds.join(", ")}`,
          duration: 10000,
        })
      } else if (!result.success) {
        toast.danger({
          title: "Launch Failed",
          description: result.error || "Unknown error occurred",
          duration: 5000,
        })
      }
    } finally {
      setIsLaunching(false)
    }
  }, [executeLaunch, invalidatePostLaunchCaches, isLaunching, trackRunLaunched])

  const handleAgentIdsChange = useAgentSelectionChange(
    selectedAgentId,
    templateData.agentIds,
    updateTemplateData,
    isLoading || Boolean(hasError),
  )

  const handleMutationIdsChange = useCallback(
    (mutations: Record<string, string[]>) => {
      updateTemplateData({ mutationIdsByAgent: mutations })
    },
    [updateTemplateData],
  )

  const handleSimulatorChange = useCallback(
    (type: SimulatorType, metadata: Record<string, unknown>) => {
      updateTemplateData({
        simulationType: type,
        simulationMetadata: metadata,
      })
    },
    [updateTemplateData],
  )

  const handlePersonaChange = useCallback(
    (personaIds: string[]) => {
      updateTemplateData({ personaIds })
    },
    [updateTemplateData],
  )

  const handleTestSetChange = useCallback(
    (testSetIds: string[]) => {
      const firstTestSetId = testSetIds[0] || ""
      const adjustedConcurrency = getAdjustedEvaluationConcurrency(
        testSetRecords,
        firstTestSetId,
        templateData.iterationCount || 1,
        templateData.concurrency || DEFAULT_CONCURRENCY,
      )

      updateTemplateData({
        testSetIds,
        concurrency: adjustedConcurrency,
        selectedTestCaseIds: getRetainedSelectedTestCaseIds(
          templateData.testSetIds,
          testSetIds,
          templateData.selectedTestCaseIds,
        ),
      })
    },
    [
      testSetRecords,
      templateData.iterationCount,
      templateData.concurrency,
      templateData.selectedTestCaseIds,
      templateData.testSetIds,
      updateTemplateData,
    ],
  )

  const handleSelectedTestCaseIdsChange = useCallback(
    (selectedTestCaseIds: string[]) => {
      updateTemplateData({
        selectedTestCaseIds:
          selectedTestCaseIds.length > 0 ? selectedTestCaseIds : undefined,
      })
    },
    [updateTemplateData],
  )

  const handleIterationCountChange = useCallback(
    (count: number) => {
      const firstTestSetId = templateData.testSetIds?.[0] || ""
      const adjustedConcurrency = getAdjustedEvaluationConcurrency(
        testSetRecords,
        firstTestSetId,
        count,
        templateData.concurrency || DEFAULT_CONCURRENCY,
      )

      updateTemplateData({
        iterationCount: count,
        concurrency: adjustedConcurrency,
      })
    },
    [
      testSetRecords,
      templateData.testSetIds,
      templateData.concurrency,
      updateTemplateData,
    ],
  )

  const handleConcurrencyChange = useCallback(
    (concurrency: number) => {
      updateTemplateData({ concurrency })
    },
    [updateTemplateData],
  )

  const handleSubSamplingChange = useCallback(
    (shouldSubSample: boolean, subSampleSize?: number) => {
      updateTemplateData({
        shouldSubSample,
        subSampleSize,
      })
    },
    [updateTemplateData],
  )

  const handleTestCaseParamsChange = useCallback(
    (params: JsonObject[]) => {
      updateTemplateData({ testCaseParamsByIteration: params })
    },
    [updateTemplateData],
  )

  const handleMetricsChange = useCallback(
    (metrics: string[]) => {
      updateTemplateData({ enabledMetrics: metrics })
    },
    [updateTemplateData],
  )

  useEffect(() => {
    if (!templateRequest.pending || dataLoading || hasError) {
      return
    }
    const template = templateRecords.find(
      (record) => record.templateId === templateRequest.id,
    )
    if (template) {
      loadTemplate(template)
    }
    setTemplateRequest((request) => ({ ...request, pending: false }))
  }, [templateRequest, dataLoading, hasError, templateRecords, loadTemplate])

  const handleTemplateSelection = useCallback(
    (templateId: string) => {
      setSelectedTemplateId(templateId)

      clearTemplate()
      const id = templateId === "none" ? "" : templateId
      setTemplateRequest({ id, pending: Boolean(id) })
    },
    [clearTemplate],
  )

  const summaryData = useMemo(
    () => buildEvaluationSummaryData(templateData, testSetRecords),
    [templateData, testSetRecords],
  )

  const isMultiRun = useMemo(
    () => isMultiRunEvaluation(templateData),
    [templateData],
  )
  const hasMultiplePersonas = useMemo(
    () => hasMultipleEvaluationPersonas(templateData),
    [templateData],
  )
  const hasMultipleTestSets = useMemo(
    () => hasMultipleEvaluationTestSets(templateData),
    [templateData],
  )

  return {
    username,
    templateData,
    metricSetupRevision,
    stableTemplateData,
    testSetRecords,
    metricRecords,
    templateRecords,
    organizationRecord,
    personaRecords,
    validationErrors,
    isLoading,
    hasError,
    templateSelectionPending: templateRequest.pending,
    templateSelectionError: templateRequest.pending && Boolean(hasError),
    retryTemplateSelection: refetchData,
    isLaunchDisabled: isLaunchDisabled || isQuotaResolving,
    isMultiRun,
    hasMultiplePersonas,
    hasMultipleTestSets,
    summaryData,
    selectedTemplateId,
    setSelectedTemplateId,
    showTemplateDropdown,
    setShowTemplateDropdown,
    showScheduleModal,
    setShowScheduleModal,
    scheduledConfig,
    setScheduledConfig,
    launchAttempted,
    isLaunching,
    runDisplayName,
    setRunDisplayName,
    runMetadataJson: runMetadata.json,
    runMetadataError: runMetadata.error,
    internalDevId,
    selectedMutationIds: templateData.mutationIdsByAgent ?? {},
    tags,
    setTags,
    createReport,
    setCreateReport,
    reportName,
    setReportName,
    handleAgentIdsChange,
    handleMutationIdsChange,
    handleSimulatorChange,
    handlePersonaChange,
    handleRunMetadataChange: runMetadata.handleChange,
    handleRunMetadataBlur: runMetadata.handleBlur,
    handleTestSetChange,
    handleSelectedTestCaseIdsChange,
    handleIterationCountChange,
    handleConcurrencyChange,
    handleSubSamplingChange,
    handleTestCaseParamsChange,
    handleMetricsChange,
    handleTemplateSelection,
    handleLaunch,
    handleLaunchAndCreateAnother,
    generateLaunchRunRequestForAgent,
  }
}
